import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getStudentInstituteRequests } from './instituteService';
import { supabase } from './supabaseClient';
import { 
  FaSchool, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUsers, 
  FaVideo, FaFileAlt, FaCheckCircle, FaTimesCircle, FaHourglassHalf,
  FaExternalLinkAlt, FaDownload, FaFilter, FaSearch
} from 'react-icons/fa';
import { format, parseISO, isPast, isToday, isTomorrow, differenceInHours } from 'date-fns';

export default function StudentInstituteSessions() {
  const { isArabic } = useOutletContext() || {};
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'upcoming', 'past', 'pending', 'accepted'
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function fetchSessions() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) {
          setLoading(false);
          return;
        }

        const { data, error } = await getStudentInstituteRequests(user.email);
        if (error) {
          console.error('Error fetching sessions:', error);
        } else {
          setSessions(data || []);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchSessions();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const getSessionStatus = (session) => {
    const sessionDate = session.preferred_date ? parseISO(session.preferred_date) : null;
    const now = new Date();

    if (session.status === 'pending') {
      return { label: isArabic ? 'قيد الانتظار' : 'Pending', color: '#f59e0b', icon: <FaHourglassHalf /> };
    }
    if (session.status === 'declined') {
      return { label: isArabic ? 'مرفوض' : 'Declined', color: '#ef4444', icon: <FaTimesCircle /> };
    }
    if (session.status === 'accepted' || session.payment_status === 'paid') {
      if (sessionDate && isPast(sessionDate) && !isToday(sessionDate)) {
        return { label: isArabic ? 'مكتمل' : 'Completed', color: '#6b7280', icon: <FaCheckCircle /> };
      }
      if (sessionDate && (isToday(sessionDate) || isTomorrow(sessionDate) || sessionDate > now)) {
        return { label: isArabic ? 'قادم' : 'Upcoming', color: '#10b981', icon: <FaCheckCircle /> };
      }
      return { label: isArabic ? 'مقبول' : 'Accepted', color: '#10b981', icon: <FaCheckCircle /> };
    }
    return { label: isArabic ? 'قيد الانتظار' : 'Pending', color: '#f59e0b', icon: <FaHourglassHalf /> };
  };

  const filteredSessions = sessions.filter(session => {
    // Filter by status
    if (filter === 'upcoming') {
      const sessionDate = session.preferred_date ? parseISO(session.preferred_date) : null;
      if (!sessionDate) return false;
      const status = getSessionStatus(session);
      return status.label === 'Upcoming' || status.label === 'قادم';
    }
    if (filter === 'past') {
      const status = getSessionStatus(session);
      return status.label === 'Completed' || status.label === 'مكتمل';
    }
    if (filter === 'pending') {
      return session.status === 'pending';
    }
    if (filter === 'accepted') {
      return session.status === 'accepted' || session.payment_status === 'paid';
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        session.title?.toLowerCase().includes(searchLower) ||
        session.institutes?.name?.toLowerCase().includes(searchLower) ||
        session.subject?.toLowerCase().includes(searchLower) ||
        session.teacher_name?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const upcomingCount = sessions.filter(s => {
    const status = getSessionStatus(s);
    return status.label === 'Upcoming' || status.label === 'قادم';
  }).length;

  const pastCount = sessions.filter(s => {
    const status = getSessionStatus(s);
    return status.label === 'Completed' || status.label === 'مكتمل';
  }).length;

  const pendingCount = sessions.filter(s => s.status === 'pending').length;

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 16, color: '#6b7280' }}>
          {isArabic ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', marginBottom: 8 }}>
        {isArabic ? 'جلساتي في المعاهد' : 'My Institute Sessions'}
      </h1>
      <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 24 }}>
        {isArabic 
          ? 'عرض وإدارة جميع جلساتك ودوراتك في المعاهد' 
          : 'View and manage all your institute sessions and courses'}
      </p>

      {/* Filters and Search */}
      <div style={{ 
        display: 'flex', 
        gap: 12, 
        marginBottom: 24,
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <FaSearch style={{ 
            position: 'absolute', 
            left: 12, 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }} />
          <input
            type="text"
            placeholder={isArabic ? 'بحث...' : 'Search...'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '2px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 14
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '8px 16px',
              background: filter === 'all' ? '#3b82f6' : '#f3f4f6',
              color: filter === 'all' ? '#fff' : '#374151',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {isArabic ? 'الكل' : 'All'} ({sessions.length})
          </button>
          <button
            onClick={() => setFilter('upcoming')}
            style={{
              padding: '8px 16px',
              background: filter === 'upcoming' ? '#10b981' : '#f3f4f6',
              color: filter === 'upcoming' ? '#fff' : '#374151',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {isArabic ? 'قادمة' : 'Upcoming'} ({upcomingCount})
          </button>
          <button
            onClick={() => setFilter('past')}
            style={{
              padding: '8px 16px',
              background: filter === 'past' ? '#6b7280' : '#f3f4f6',
              color: filter === 'past' ? '#fff' : '#374151',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {isArabic ? 'مكتملة' : 'Completed'} ({pastCount})
          </button>
          <button
            onClick={() => setFilter('pending')}
            style={{
              padding: '8px 16px',
              background: filter === 'pending' ? '#f59e0b' : '#f3f4f6',
              color: filter === 'pending' ? '#fff' : '#374151',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {isArabic ? 'قيد الانتظار' : 'Pending'} ({pendingCount})
          </button>
        </div>
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          background: '#fff',
          borderRadius: 16,
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <FaSchool size={48} color="#d1d5db" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            {isArabic ? 'لا توجد جلسات' : 'No Sessions Found'}
          </div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>
            {isArabic 
              ? 'لم تقم بحجز أي جلسات في المعاهد بعد' 
              : 'You haven\'t booked any institute sessions yet'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredSessions.map(session => {
            const status = getSessionStatus(session);
            const sessionDate = session.preferred_date ? parseISO(session.preferred_date) : null;
            const isUpcoming = sessionDate && (isToday(sessionDate) || sessionDate > new Date());
            const hoursUntil = sessionDate ? differenceInHours(sessionDate, new Date()) : null;

            return (
              <div
                key={session.id}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  border: '1px solid #e5e7eb',
                  borderLeft: `4px solid ${status.color}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      {session.institutes?.logo_url ? (
                        <img
                          src={session.institutes.logo_url}
                          alt={session.institutes.name}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <FaSchool size={24} color="#3b82f6" />
                      )}
                      <div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
                          {session.title}
                        </h3>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                          {session.institutes?.name || (isArabic ? 'معهد' : 'Institute')}
                        </div>
                      </div>
                      <span style={{
                        background: status.color + '20',
                        color: status.color,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '4px 12px',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}>
                        {status.icon}
                        {status.label}
                      </span>
                    </div>

                    {/* Session Details */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
                      {sessionDate && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
                          <FaCalendarAlt color="#3b82f6" />
                          <span>{formatDate(session.preferred_date)}</span>
                        </div>
                      )}
                      {session.preferred_time_from && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
                          <FaClock color="#3b82f6" />
                          <span>
                            {formatTime(session.preferred_time_from)}
                            {session.preferred_time_to && ` - ${formatTime(session.preferred_time_to)}`}
                          </span>
                        </div>
                      )}
                      {session.format === 'in_person' && session.place && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
                          <FaMapMarkerAlt color="#3b82f6" />
                          <span>{session.place}</span>
                        </div>
                      )}
                      {session.format === 'online' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#10b981' }}>
                          <FaVideo color="#10b981" />
                          <span>{isArabic ? 'أونلاين' : 'Online'}</span>
                        </div>
                      )}
                      {session.teacher_name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
                          <FaUsers color="#3b82f6" />
                          <span>{session.teacher_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Type Badges */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                      <span style={{
                        background: session.session_type === 'course' ? '#8b5cf6' : '#3b82f6',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: 6
                      }}>
                        {session.session_type === 'course' 
                          ? (isArabic ? 'دورة' : 'Course')
                          : (isArabic ? 'جلسة' : 'Session')
                        }
                      </span>
                      {session.payment_status === 'paid' && (
                        <span style={{
                          background: '#10b981',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '4px 10px',
                          borderRadius: 6
                        }}>
                          {isArabic ? 'مدفوع' : 'Paid'}
                        </span>
                      )}
                    </div>

                    {/* Meeting Link for Online Sessions */}
                    {session.format === 'online' && session.meeting_link && isUpcoming && (
                      <div style={{
                        marginTop: 12,
                        padding: 12,
                        background: '#f0f9ff',
                        borderRadius: 8,
                        border: '1px solid #bae6fd'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0369a1', marginBottom: 4 }}>
                              {isArabic ? 'رابط الاجتماع' : 'Meeting Link'}
                            </div>
                            <a
                              href={session.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: 12,
                                color: '#0284c7',
                                textDecoration: 'none',
                                wordBreak: 'break-all'
                              }}
                            >
                              {session.meeting_link}
                            </a>
                          </div>
                          <a
                            href={session.meeting_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '8px 16px',
                              background: '#0284c7',
                              color: '#fff',
                              borderRadius: 6,
                              fontSize: 13,
                              fontWeight: 600,
                              textDecoration: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            <FaVideo size={12} />
                            {isArabic ? 'انضم الآن' : 'Join Now'}
                            <FaExternalLinkAlt size={10} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Upcoming Reminder */}
                    {isUpcoming && hoursUntil !== null && hoursUntil <= 24 && hoursUntil > 0 && (
                      <div style={{
                        marginTop: 12,
                        padding: 10,
                        background: '#fef3c7',
                        borderRadius: 8,
                        fontSize: 13,
                        color: '#92400e'
                      }}>
                        {isArabic 
                          ? `⏰ الجلسة تبدأ خلال ${hoursUntil} ساعة`
                          : `⏰ Session starts in ${hoursUntil} hour${hoursUntil !== 1 ? 's' : ''}`
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

